import React, {useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {Formik} from 'formik';
import {Form} from 'react-bootstrap';
import * as yup from 'yup';
import {toast} from 'react-toastify';
import {ACCOUNT_API} from '@app/utils/apiUrl';
import {
    LOGIN_SSO_ORIGIN_KEY,
    accountGender,
    messageError,
    objectAccountGender
} from '@app/utils/define';
import {URI_INTERNAL} from '@app/utils/pathLocations';
import Cookies from 'js-cookie';

const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

const defaultRegisterValues = {
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    middleName: '',
    lastName: '',
    address: '',
    accountGender: accountGender.other,
    dateOfBirth: '',
    issuanceDateOfIdentityCard: '',
    issuancePlaceOfIdentityCard: '',
    // roleCode: 'patient'
};

const registerSchema = yup.object().shape({
    username: yup
        .string()
        .matches(/^\d{12}$/, 'Căn cước công dân phải gồm đúng 12 chữ số')
        .required('Vui lòng nhập căn cước công dân'),
    email: yup
        .string()
        .email('Địa chỉ email không hợp lệ')
        .required('Vui lòng nhập email'),
    firstName: yup.string().required('Vui lòng nhập tên'),
    lastName: yup.string().required('Vui lòng nhập họ'),
    password: yup
        .string()
        .min(6, messageError.minLength6Message)
        .max(30, messageError.maxLength30Message)
        .min(8, messageError.minLength8Message)
        .max(30, messageError.maxLength30Message)
        .matches(/[A-Z]/, messageError.passwordUppercase)
        .matches(/[0-9]/, messageError.passwordNumber)
        .matches(SPECIAL_CHAR_REGEX, messageError.passwordSpecial)
        .required(messageError.emptyMessage),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('password'), null], 'Mật khẩu xác nhận không khớp')
        .required('Vui lòng nhập lại mật khẩu'),
    middleName: yup.string(),
    address: yup.string(),
    accountGender: yup.string().required('Vui lòng chọn giới tính'),
    dateOfBirth: yup.string().nullable(),
    // issuanceDateOfIdentityCard: yup.string().nullable(),
    // issuancePlaceOfIdentityCard: yup.string(),
    // roleCode: yup.string()
});

const otpSchema = yup.object().shape({
    email: yup
        .string()
        .email('Địa chỉ email không hợp lệ')
        .required('Vui lòng nhập email'),
    otpCode: yup
        .string()
        .matches(/^\d{6}$/, 'Mã OTP phải gồm 6 chữ số')
        .required('Vui lòng nhập mã OTP')
});

const toNullableDate = (value) => (value ? value : null);

const getLoginSsoUrl = () => {
    const origin =
        Cookies.get(LOGIN_SSO_ORIGIN_KEY) || window.location.origin;

    return `${origin}${URI_INTERNAL.LOGIN_SSO_PATH}`;
};

const publicPost = async (url, payload) => {
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    let data = null;
    try {
        data = await response.json();
    } catch {
        data = null;
    }

    if (!response.ok) {
        throw data || {message: 'Không thể xử lý yêu cầu, vui lòng thử lại.'};
    }

    return data;
};

const RegisterAccount = () => {
    const [step, setStep] = useState('register');
    const [registerValues, setRegisterValues] = useState(
        defaultRegisterValues
    );
    const [registeredEmail, setRegisteredEmail] = useState('');
    const [createdAccount, setCreatedAccount] = useState(null);
    const [isSubmittingRegister, setSubmittingRegister] = useState(false);
    const [isSubmittingOtp, setSubmittingOtp] = useState(false);

    const history = useHistory();

    document.getElementById('root').classList = 'hold-transition register-page';

    const handleRegister = async (values, actions) => {
        setSubmittingRegister(true);
        actions.setStatus(null);

        try {
            const payload = {
                Username: values.username.trim(),
                Email: values.email.trim(),
                Password: values.password,
                FirstName: values.firstName.trim(),
                MiddleName: values.middleName.trim(),
                LastName: values.lastName.trim(),
                Address: values.address.trim(),
                AccountGender: values.accountGender,
                DateOfBirth: toNullableDate(values.dateOfBirth),
                IssuanceDateOfIdentityCard: toNullableDate(
                    values.issuanceDateOfIdentityCard
                ),
                IssuancePlaceOfIdentityCard:
                    values.issuancePlaceOfIdentityCard.trim()
            };

            const result = await publicPost(
                ACCOUNT_API.REGISTER_ACCOUNT,
                payload
            );

            const email = values.email.trim();
            setRegisterValues({...values});
            setRegisteredEmail(email);
            setStep('otp');
            toast.success(result?.message || 'Đã gửi OTP về email.');
        } catch (error) {
            const message =
                error?.message || 'Đăng ký chưa thành công, vui lòng thử lại.';
            actions.setStatus(message);
            toast.error(message);
        } finally {
            setSubmittingRegister(false);
        }
    };

    const handleConfirmOtp = async (values, actions) => {
        setSubmittingOtp(true);
        actions.setStatus(null);

        try {
            const result = await publicPost(
                ACCOUNT_API.CONFIRM_REGISTER_OTP,
                {
                    Email: values.email.trim(),
                    OtpCode: values.otpCode.trim()
                }
            );

            setCreatedAccount({
                username: result?.username || values.email.trim()
            });
            setStep('done');
            toast.success(
                result?.message ||
                    'Xác nhận OTP thành công, tài khoản đã được tạo.'
            );
        } catch (error) {
            const message =
                error?.message || 'Xác nhận OTP chưa thành công.';
            actions.setStatus(message);
            toast.error(message);
        } finally {
            setSubmittingOtp(false);
        }
    };

    return (
        <>
            <div className="register-account-page">
                <div className="register-account-shell">
                    <aside className="register-account-side">
                        <div className="register-account-step">
                            {step === 'register' && 'Bước 1/2'}
                            {step === 'otp' && 'Bước 2/2'}
                            {step === 'done' && 'Hoàn tất'}
                        </div>
                        <h1 className="register-account-title">
                            Đăng ký tài khoản
                        </h1>
                        <p className="register-account-copy">
                            Tạo tài khoản bằng căn cước công dân và email. Hệ
                            thống sẽ gửi OTP để xác nhận trước khi cấp tài khoản.
                        </p>
                    </aside>

                    <main className="register-account-main">
                        {step === 'register' && (
                            <Formik
                                validationSchema={registerSchema}
                                initialValues={registerValues}
                                onSubmit={handleRegister}
                            >
                                {({
                                    handleSubmit,
                                    handleChange,
                                    values,
                                    errors,
                                    touched,
                                    status
                                }) => (
                                    <Form
                                        onSubmit={handleSubmit}
                                        autoComplete="off"
                                    >
                                        {status && (
                                            <div className="register-account-error">
                                                {status}
                                            </div>
                                        )}

                                        <div className="register-account-grid">
                                            <Form.Group>
                                                <Form.Label>
                                                    Username / Căn cước công dân
                                                </Form.Label>
                                                <Form.Control
                                                    name="username"
                                                    type="tel"
                                                    value={values.username}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.username &&
                                                        !!errors.username
                                                    }
                                                    placeholder="12 chữ số"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    maxLength={12}
                                                    autoComplete="off"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.username}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control
                                                    name="email"
                                                    type="email"
                                                    value={values.email}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.email &&
                                                        !!errors.email
                                                    }
                                                    placeholder="name@example.com"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.email}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>Mật khẩu</Form.Label>
                                                <Form.Control
                                                    name="password"
                                                    type="password"
                                                    value={values.password}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.password &&
                                                        !!errors.password
                                                    }
                                                    placeholder="Từ 6 đến 30 ký tự"
                                                    autoComplete="new-password"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.password}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>
                                                    Xác nhận mật khẩu
                                                </Form.Label>
                                                <Form.Control
                                                    name="confirmPassword"
                                                    type="password"
                                                    value={values.confirmPassword}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.confirmPassword &&
                                                        !!errors.confirmPassword
                                                    }
                                                    placeholder="Nhập lại mật khẩu"
                                                    autoComplete="new-password"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.confirmPassword}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>Họ</Form.Label>
                                                <Form.Control
                                                    name="lastName"
                                                    value={values.lastName}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.lastName &&
                                                        !!errors.lastName
                                                    }
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.lastName}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>Tên</Form.Label>
                                                <Form.Control
                                                    name="firstName"
                                                    value={values.firstName}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.firstName &&
                                                        !!errors.firstName
                                                    }
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.firstName}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>Tên đệm</Form.Label>
                                                <Form.Control
                                                    name="middleName"
                                                    value={values.middleName}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>Giới tính</Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    name="accountGender"
                                                    value={values.accountGender}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.accountGender &&
                                                        !!errors.accountGender
                                                    }
                                                >
                                                    {objectAccountGender.map(
                                                        (item) => (
                                                            <option
                                                                key={item.value}
                                                                value={item.value}
                                                            >
                                                                {item.label}
                                                            </option>
                                                        )
                                                    )}
                                                </Form.Control>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.accountGender}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Label>
                                                    Ngày sinh
                                                </Form.Label>
                                                <Form.Control
                                                    name="dateOfBirth"
                                                    type="date"
                                                    value={values.dateOfBirth}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>

                                            {/* <Form.Group>
                                                <Form.Label>
                                                    Ngày cấp CCCD
                                                </Form.Label>
                                                <Form.Control
                                                    name="issuanceDateOfIdentityCard"
                                                    type="date"
                                                    value={
                                                        values.issuanceDateOfIdentityCard
                                                    }
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>

                                            <Form.Group className="register-account-field-full">
                                                <Form.Label>
                                                    Nơi cấp CCCD
                                                </Form.Label>
                                                <Form.Control
                                                    name="issuancePlaceOfIdentityCard"
                                                    value={
                                                        values.issuancePlaceOfIdentityCard
                                                    }
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>

                                            <Form.Group className="register-account-field-full">
                                                <Form.Label>Địa chỉ</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    name="address"
                                                    value={values.address}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group> */}
                                        </div>

                                        <div className="register-account-actions">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={() => history.goBack()}
                                            >
                                                {/* {t('login.button.signIn.label')} */}
                                                Quay lại đăng nhập
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={isSubmittingRegister}
                                            >
                                                {isSubmittingRegister
                                                    ? 'Đang gửi OTP...'
                                                    : 'Gửi OTP xác nhận'}
                                            </button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        )}

                        {step === 'otp' && (
                            <Formik
                                enableReinitialize
                                validationSchema={otpSchema}
                                initialValues={{
                                    email: registeredEmail,
                                    otpCode: ''
                                }}
                                onSubmit={handleConfirmOtp}
                            >
                                {({
                                    handleSubmit,
                                    handleChange,
                                    values,
                                    errors,
                                    touched,
                                    status
                                }) => (
                                    <Form onSubmit={handleSubmit}>
                                        {status && (
                                            <div className="register-account-error">
                                                {status}
                                            </div>
                                        )}

                                        <div className="register-account-grid">
                                            <Form.Group className="register-account-field-full">
                                                <Form.Label>Email nhận OTP</Form.Label>
                                                <Form.Control
                                                    name="email"
                                                    type="email"
                                                    value={values.email}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.email &&
                                                        !!errors.email
                                                    }
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.email}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="register-account-field-full">
                                                <Form.Label>Mã OTP</Form.Label>
                                                <Form.Control
                                                    name="otpCode"
                                                    value={values.otpCode}
                                                    onChange={handleChange}
                                                    isInvalid={
                                                        touched.otpCode &&
                                                        !!errors.otpCode
                                                    }
                                                    placeholder="6 chữ số"
                                                    inputMode="numeric"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.otpCode}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </div>

                                        <div className="register-account-actions">
                                            <button
                                                type="button"
                                                className="btn btn-link px-0"
                                                onClick={() => setStep('register')}
                                            >
                                                Sửa thông tin đăng ký
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={isSubmittingOtp}
                                            >
                                                {isSubmittingOtp
                                                    ? 'Đang xác nhận...'
                                                    : 'Xác nhận OTP'}
                                            </button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        )}

                        {step === 'done' && (
                            <>
                                <div className="register-account-note">
                                    Tài khoản đã được tạo thành công.
                                    <br />
                                    Tên đăng nhập: <b>{createdAccount?.username}</b>
                                </div>
                                <div className="register-account-actions">
                                    <span />
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            window.location.href =
                                                getLoginSsoUrl();
                                        }}
                                    >
                                        Đến trang đăng nhập
                                    </button>
                                </div>
                            </>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
};

export default RegisterAccount;
